import { FC, useState, useCallback, useEffect, useMemo } from 'react';
import { GoogleMap as ReactGoogleMap, GoogleMapProps } from '@react-google-maps/api';
import { useGoogleApiContext } from '../../../contexts/google-api.context';
import { eventMarker, darkThemeStyles } from './map-block.styles';

const containerStyle = {
	width: '100%',
	height: '400px',
	borderRadius: '8px',
};

const googleMapOptions: google.maps.MapOptions = {
	disableDefaultUI: true,
	zoomControl: false,
	gestureHandling: 'cooperative',
	restriction: {
		latLngBounds: {
			north: 90 - 5,
			south: -90 + 5,
			west: -180,
			east: 180,
		},
	},
};

export interface MapBlockProps extends GoogleMapProps {
	markers?: Array<{
		position?: {
			lat: number;
			lng: number;
		};
		addressName?: string;
		size?: number;
		element?: string;
	}>;
	width?: string;
	height?: string;
	variant?: 'default' | 'dark';
}

const MapBlock: FC<MapBlockProps> = ({
	markers = [],
	width,
	height,
  variant = 'default',
	zoom = 6,
	...restProps
}) => {
	const { isLoaded } = useGoogleApiContext();

	const [map, setMap] = useState<google.maps.Map | null>(null);
	const [bounsIsChanged, setBounsIsChanged] = useState(false);
	const [googleMarkers, setGoogleMarkers] = useState<google.maps.Marker[]>([]);

	const bounds = useMemo(() => {
		if (!map) {
			return;
		}

		const bounds = new google.maps.LatLngBounds();
		markers.forEach(({ position }) => {
			bounds.extend(position);
		});

		return bounds;
	}, [map, markers]);

	const onLoad = useCallback((map: google.maps.Map) => {
		setMap(map);
	}, []);

	const onUnmount = useCallback(() => {
		setMap(null);
	}, []);

	const onBoundsChanged = useCallback(() => {
		if (bounsIsChanged && map && map.getZoom() > zoom) {
			map.setZoom(zoom);
			setBounsIsChanged(false);
		}
	}, [map, zoom, bounsIsChanged]);

	useEffect(() => {
		if (!map || !bounds || markers.length <= 1) {
			return;
		}

		map.fitBounds(bounds, 60);
		setBounsIsChanged(false);
	}, [markers, map, zoom, bounds]);

	useEffect(() => {
		if (!map) {
			return;
		}

		const googleMarkers = (markers ?? []).map((marker) => {
			const { size, addressName, position, element } = marker;
			const markerSVG = window.btoa(element ?? eventMarker);
			const markerWidth = size || 60;
			const markerHeight = size || 60;

			const googleMarker = new google.maps.Marker({
				map,
				position,
				zIndex: Number(google.maps.Marker.MAX_ZINDEX),
				icon: {
					url: `data:image/svg+xml;base64,${markerSVG}`,
					scaledSize: new google.maps.Size(markerWidth, markerHeight, 'px', 'px'),
				},
				optimized: false,
			});

			if (addressName) {
				const googleInfoWindow = new google.maps.InfoWindow({
					content: `<div style="font-weight: 600">${addressName}</div>`,
				});

				googleMarker.addListener("click", () => googleInfoWindow.open(map, googleMarker));
			}

			return googleMarker;
		});

		setGoogleMarkers((oldGoogleMarkers) => {
			oldGoogleMarkers.map((marker) => {
				marker.setMap(null);
			});

			return googleMarkers;
		});
	}, [map, markers]);

	if (!isLoaded) {
		return <div />;
	}

	return (
		<ReactGoogleMap
			mapContainerStyle={{
				...containerStyle,
				width: width || containerStyle.width,
				height: height || containerStyle.height,
			}}
			center={bounds?.getCenter()}
			zoom={zoom}
			onLoad={onLoad}
			onBoundsChanged={onBoundsChanged}
			onUnmount={onUnmount}
			options={{
				styles: variant === 'dark' ? darkThemeStyles : undefined,
				...googleMapOptions,
				...restProps.options,
			}}
			{...restProps}
		>
			{/* Child components, such as markers, info windows, etc. */}
		</ReactGoogleMap>
	);
};

export default MapBlock;
